import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { toast } from "sonner";
import WhatsAppPanel from "../components/admin/WhatsAppPanel";
import AlertsPanel from "../components/admin/AlertsPanel";
import LiveControlPanel from "../components/admin/LiveControlPanel";
import ScoreboardPanel from "../components/admin/ScoreboardPanel";

const PHASES = ["poule", "quart", "demi", "finale"];
const STATUSES = ["upcoming", "live", "finished"];

export default function Admin() {
  const [tab, setTab] = useState("teams");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12" data-testid="admin-page">
      <p className="text-xs tracking-[0.3em] uppercase text-[#FF6B1A] mb-3">Dashboard</p>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <h1 className="font-display text-4xl sm:text-6xl leading-none">ADMINISTRATION</h1>
        <LiveScreenControl />
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10" data-testid="admin-stats">
          <Stat k={stats.teams_total} v="Équipes" />
          <Stat k={stats.teams_pending} v="En attente" color="#FF9F0A" />
          <Stat k={stats.teams_approved} v="Validées" color="#FF6B1A" />
          <Stat k={stats.matches_total} v="Matchs" />
          <Stat k={stats.matches_finished} v="Terminés" color="#FF6B1A" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 mb-8 flex-wrap" data-testid="admin-tabs">
        {[
          { v: "teams", label: "Équipes" },
          { v: "regie", label: "🔴 Régie Live" },
          { v: "tableau", label: "Tableau" },
          { v: "whatsapp", label: "WhatsApp" },
          { v: "alerts", label: "Alertes" },
          { v: "matches", label: "Matchs" },
          { v: "bracket", label: "Bracket" },
          { v: "standings", label: "Classement" },
          { v: "gallery", label: "Galerie" },
          { v: "headliners", label: "Têtes d'affiche" },
          { v: "partners", label: "Partenaires" },
          { v: "export", label: "Export" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold border-b-2 transition-colors ${
              tab === t.v ? "border-[#FF6B1A] text-[#FF6B1A]" : "border-transparent text-white/60 hover:text-white"
            }`}
            data-testid={`admin-tab-${t.v}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "teams" && <TeamsPanel onChange={loadStats} />}
      {tab === "regie" && <LiveControlPanel />}
      {tab === "tableau" && <ScoreboardPanel />}
      {tab === "whatsapp" && <WhatsAppPanel />}
      {tab === "alerts" && <AlertsPanel />}
      {tab === "matches" && <MatchesPanel onChange={loadStats} />}
      {tab === "bracket" && <BracketPanel onChange={loadStats} />}
      {tab === "standings" && <StandingsPanel />}
      {tab === "gallery" && <GalleryPanel />}
      {tab === "headliners" && <HeadlinersPanel />}
      {tab === "partners" && <PartnersPanel />}
      {tab === "export" && <ExportPanel />}
    </div>
  );
}

function Stat({ k, v, color = "#FFFFFF" }) {
  return (
    <div className="uf-card p-4">
      <p className="font-display text-4xl" style={{ color }}>{k}</p>
      <p className="text-xs tracking-widest uppercase text-white/50 mt-1">{v}</p>
    </div>
  );
}

function LiveScreenControl() {
  const [settings, setSettings] = useState({ mode: "matches", discipline: null });
  const [timer, setTimer] = useState(null);
  const [preset, setPreset] = useState("600");

  useEffect(() => {
    api.get("/live-settings").then((r) => setSettings(r.data));
    api.get("/timer").then((r) => setTimer(r.data));
  }, []);

  const update = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await api.patch("/admin/live-settings", { mode: next.mode, discipline: next.discipline || null });
      toast.success("Écran live mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const timerAction = async (action, extra = {}) => {
    try {
      const { data } = await api.post("/admin/timer", { action, ...extra });
      setTimer(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const PRESET_LABELS = { 600: "Poule 10 min", 720: "Finale 12 min", 360: "Foot 6 min" };
  const timerActive = timer && (timer.running || timer.paused_remaining !== null);

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center gap-2 flex-wrap" data-testid="live-screen-control">
        <span className="text-[10px] uppercase tracking-widest text-white/50">Écran live :</span>
        <select
          className="uf-input !py-2 !px-3 text-xs"
          value={settings.mode}
          onChange={(e) => update({ mode: e.target.value })}
          data-testid="live-mode-select"
        >
          <option value="matches">Matchs & scores</option>
          <option value="teams">Présentation des équipes</option>
        </select>
        {settings.mode === "teams" && (
          <select
            className="uf-input !py-2 !px-3 text-xs"
            value={settings.discipline || ""}
            onChange={(e) => update({ discipline: e.target.value || null })}
            data-testid="live-discipline-select"
          >
            <option value="">Toutes disciplines</option>
            <option value="basket">Basket 3x3</option>
            <option value="foot">Foot 5v5</option>
          </select>
        )}
        <a
          href="/live"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-brutal !py-2.5 !px-5 !text-xs"
          data-testid="open-live-screen-btn"
        >
          📺 Ouvrir l'écran
        </a>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="timer-control">
        <span className="text-[10px] uppercase tracking-widest text-white/50">Chrono :</span>
        <select className="uf-input !py-2 !px-3 text-xs" value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="timer-preset-select">
          <option value="600">Poule — 10 min</option>
          <option value="720">Finale — 12 min</option>
          <option value="360">Foot — 6 min</option>
        </select>
        <button
          onClick={() => timerAction("start", { duration: Number(preset), label: PRESET_LABELS[preset] })}
          className="text-xs uppercase tracking-widest font-bold bg-[#2EE59D] text-black px-3 py-2 rounded-full hover:brightness-110 transition-all"
          data-testid="timer-start-btn"
        >
          ▶ Démarrer
        </button>
        {timer?.running ? (
          <button
            onClick={() => timerAction("pause")}
            className="text-xs uppercase tracking-widest font-bold bg-[#FF9F0A] text-black px-3 py-2 rounded-full hover:brightness-110 transition-all"
            data-testid="timer-pause-btn"
          >
            ⏸ Pause
          </button>
        ) : timer?.paused_remaining !== null && timer?.paused_remaining !== undefined ? (
          <button
            onClick={() => timerAction("resume")}
            className="text-xs uppercase tracking-widest font-bold bg-[#FFB800] text-black px-3 py-2 rounded-full hover:brightness-110 transition-all"
            data-testid="timer-resume-btn"
          >
            ▶ Reprendre
          </button>
        ) : null}
        {timerActive && (
          <button
            onClick={() => timerAction("reset")}
            className="text-xs uppercase tracking-widest border border-[#FF3B30]/60 text-[#FF3B30] px-3 py-2 rounded-full hover:bg-[#FF3B30] hover:text-black transition-colors"
            data-testid="timer-reset-btn"
          >
            ✕ Stop
          </button>
        )}
      </div>
    </div>
  );
}

function TeamsPanel({ onChange }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teams");
      setTeams(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/teams/${id}`, patch);
      toast.success("Équipe mise à jour");
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette équipe et ses matchs ?")) return;
    try {
      await api.delete(`/admin/teams/${id}`);
      toast.success("Équipe supprimée");
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-white/50">Chargement…</p>;

  const teamsWithEmail = teams.filter((t) => (t.captain_email || "").trim());
  const sendGroupMail = (statusFilter) => {
    const targets = teamsWithEmail.filter((t) => !statusFilter || t.status === statusFilter);
    if (targets.length === 0) {
      toast.error("Aucune équipe avec email pour cette sélection");
      return;
    }
    const emails = targets.map((t) => t.captain_email).join(",");
    const subject = encodeURIComponent("Urban Fest 2026 — Confirmation de participation avant jeudi 27 août");
    const body = encodeURIComponent(
      `Bonjour,

Merci pour votre inscription au tournoi Urban Fest 2026 (29 & 30 août 2026, Quartier Jean-Yole, La Roche-sur-Yon).

Pour valider définitivement la participation de votre équipe, merci de nous confirmer votre présence par retour de message avant le JEUDI 27 AOÛT.

Sans confirmation à cette date, votre place pourra être attribuée à une équipe en liste d'attente.

Rappels utiles :
- Basket 3v3 : 3 titulaires + 1 remplaçant (4 joueurs max)
- Football 5v5 : 5 titulaires + 2 remplaçants (7 joueurs max) — 16 équipes maximum
- Joueurs mineurs : autorisation parentale signée à apporter le jour J

Site : https://urbanfest85.com
Contact : 06 95 62 29 53 · actionimpactjeunesse@gmail.com
Instagram : @action.impact.jeunesse

Sportivement,
L'équipe Action Impact Jeunesse`
    );
    const mailtoUrl = `mailto:actionimpactjeunesse@gmail.com?bcc=${emails}&subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;
    toast.success(`Ouverture du mail pour ${targets.length} équipe${targets.length > 1 ? "s" : ""}`);
  };

  return (
    <div className="space-y-4">
      <PoolAssigner onDone={() => { load(); onChange(); }} />
      <div className="uf-card overflow-x-auto" data-testid="admin-teams-table">
      <div className="p-4 flex items-center gap-2 flex-wrap border-b border-white/10 bg-black/40" data-testid="teams-mail-actions">
        <span className="text-xs tracking-widest uppercase text-white/50 mr-2">
          Emails ({teamsWithEmail.length} avec email) :
        </span>
        <button
          type="button"
          onClick={() => sendGroupMail(null)}
          className="text-xs uppercase tracking-widest border border-[#FF6B1A]/60 text-[#FF6B1A] px-3 py-1 hover:bg-[#FF6B1A] hover:text-black rounded-full transition-colors"
          data-testid="mail-all-btn"
        >
          ✉ Toutes ({teamsWithEmail.length})
        </button>
        <button
          type="button"
          onClick={() => sendGroupMail("pending")}
          className="text-xs uppercase tracking-widest border border-[#FF9F0A]/60 text-[#FF9F0A] px-3 py-1 hover:bg-[#FF9F0A] hover:text-black rounded-full transition-colors"
          data-testid="mail-pending-btn"
        >
          ✉ En attente ({teamsWithEmail.filter((t) => t.status === "pending").length})
        </button>
        <button
          type="button"
          onClick={() => sendGroupMail("approved")}
          className="text-xs uppercase tracking-widest border border-[#2EE59D]/50 text-[#2EE59D] px-3 py-1 hover:bg-[#2EE59D] hover:text-black rounded-full transition-colors"
          data-testid="mail-approved-btn"
        >
          ✉ Validées ({teamsWithEmail.filter((t) => t.status === "approved").length})
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-black border-b border-white/20 text-xs tracking-widest uppercase text-white/50">
          <tr>
            <th className="text-left p-3">Équipe</th>
            <th className="text-left p-3">Quartier</th>
            <th className="text-left p-3">Discipline</th>
            <th className="text-left p-3">Capitaine</th>
            <th className="text-left p-3">Statut</th>
            <th className="text-left p-3">Pool</th>
            <th className="text-right p-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {teams.length === 0 ? (
            <tr><td colSpan={7} className="text-center p-10 text-white/40">Aucune équipe inscrite.</td></tr>
          ) : teams.map((t) => (
            <tr key={t.id} className="border-b border-white/5" data-testid={`admin-team-${t.id}`}>
              <td className="p-3">
                <input
                  className="uf-input !py-1 !px-2 text-sm font-bold w-40"
                  defaultValue={t.name}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== t.name) update(t.id, { name: v });
                  }}
                  data-testid={`team-name-${t.id}`}
                />
              </td>
              <td className="p-3">{t.quartier}</td>
              <td className="p-3 uppercase text-xs">{t.discipline === "basket" ? "Basket" : "Foot"}</td>
              <td className="p-3">
                <div>{t.captain_name}</div>
                <div className="text-white/50 text-xs">{t.captain_phone}</div>
              </td>
              <td className="p-3">
                <select
                  className="uf-input !py-1 !px-2 text-xs"
                  value={t.status}
                  onChange={(e) => update(t.id, { status: e.target.value })}
                  data-testid={`team-status-${t.id}`}
                >
                  <option value="pending">En attente</option>
                  <option value="approved">Validée</option>
                  <option value="rejected">Refusée</option>
                </select>
              </td>
              <td className="p-3">
                <input
                  className="uf-input !py-1 !px-2 text-xs w-20"
                  defaultValue={t.pool || ""}
                  placeholder="A/B/…"
                  onBlur={(e) => e.target.value !== (t.pool || "") && update(t.id, { pool: e.target.value || null })}
                  data-testid={`team-pool-${t.id}`}
                />
              </td>
              <td className="p-3 text-right">
                <button
                  onClick={() => remove(t.id)}
                  className="text-[#FF3B30] text-xs uppercase tracking-widest hover:underline"
                  data-testid={`delete-team-${t.id}`}
                >
                  Supprimer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

function PoolAssigner({ onDone }) {
  const [form, setForm] = useState({ discipline: "foot", num_pools: 3, generate_matches: true });
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!window.confirm(`Répartir automatiquement les équipes ${form.discipline === "foot" ? "Foot" : "Basket"} validées en ${form.num_pools} poules ?${form.generate_matches ? " Les matchs de poule à venir seront régénérés." : ""}`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/pools/assign", form);
      const detail = Object.entries(data.pools).map(([p, ts]) => `Poule ${p} : ${ts.length} éq.`).join(" · ");
      toast.success(`${data.teams_assigned} équipes réparties (${detail})${data.matches_created ? ` — ${data.matches_created} matchs créés` : ""}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="uf-card p-4 flex items-center gap-3 flex-wrap" data-testid="pool-assigner">
      <span className="text-xs uppercase tracking-widest text-white/50">Répartition automatique :</span>
      <select className="uf-input !py-2 !px-3 text-xs" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} data-testid="pool-discipline-select">
        <option value="foot">Foot 5v5</option>
        <option value="basket">Basket 3x3</option>
      </select>
      <select className="uf-input !py-2 !px-3 text-xs" value={form.num_pools} onChange={(e) => setForm({ ...form, num_pools: Number(e.target.value) })} data-testid="pool-count-select">
        {[2, 3, 4].map((n) => <option key={n} value={n}>{n} poules</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
        <input
          type="checkbox"
          checked={form.generate_matches}
          onChange={(e) => setForm({ ...form, generate_matches: e.target.checked })}
          data-testid="pool-generate-matches-checkbox"
        />
        Générer les matchs de poule
      </label>
      <button onClick={run} disabled={busy} className="btn-brutal !py-2 !px-4 !text-xs disabled:opacity-50" data-testid="pool-assign-btn">
        {busy ? "Répartition…" : "⚡ Répartir en poules"}
      </button>
      <button
        onClick={async () => {
          if (!window.confirm("Importer la fiche papier foot (11 équipes, poules A/B/C, scores joués) ? Les équipes déjà existantes seront mises à jour, rien n'est supprimé.")) return;
          try {
            const { data } = await api.post("/admin/seed-fiche-foot");
            toast.success(`Fiche importée : ${data.total_teams} équipes, ${data.total_matches} matchs (dont ${data.teams_created} équipes et ${data.matches_created} matchs créés)`);
            onDone();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        }}
        className="text-xs uppercase tracking-widest border border-[#2EE59D]/60 text-[#2EE59D] px-4 py-2 rounded-full hover:bg-[#2EE59D] hover:text-black transition-colors"
        data-testid="seed-fiche-btn"
      >
        📋 Importer la fiche foot (poules A/B/C)
      </button>
    </div>
  );
}

function MatchesPanel({ onChange }) {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({
    discipline: "basket", phase: "poule", pool: "", team_a_id: "", team_b_id: "", scheduled_at: "",
  });

  const load = useCallback(async () => {
    const [tr, mr] = await Promise.all([
      api.get("/admin/teams"),
      api.get("/matches"),
    ]);
    setTeams(tr.data.filter((t) => t.status === "approved"));
    setMatches(mr.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredTeams = teams.filter((t) => t.discipline === form.discipline);

  const create = async (e) => {
    e.preventDefault();
    if (!form.team_a_id || !form.team_b_id || form.team_a_id === form.team_b_id) {
      toast.error("Choisis deux équipes différentes");
      return;
    }
    try {
      await api.post("/admin/matches", {
        ...form,
        pool: form.pool || null,
        scheduled_at: form.scheduled_at || null,
      });
      toast.success("Match créé");
      setForm({ ...form, team_a_id: "", team_b_id: "", pool: "", scheduled_at: "" });
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/matches/${id}`, body);
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce match ?")) return;
    await api.delete(`/admin/matches/${id}`);
    toast.success("Match supprimé");
    await load();
    onChange();
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || id.slice(0, 8);

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="uf-card p-6" data-testid="create-match-form">
        <h3 className="font-display text-2xl mb-4">Nouveau match</h3>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <select className="uf-input sm:col-span-1" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value, team_a_id: "", team_b_id: "" })} data-testid="match-discipline">
            <option value="basket">Basket</option>
            <option value="foot">Foot</option>
          </select>
          <select className="uf-input sm:col-span-1" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} data-testid="match-phase">
            {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className="uf-input sm:col-span-1" placeholder="Poule (opt)" value={form.pool} onChange={(e) => setForm({ ...form, pool: e.target.value })} data-testid="match-pool" />
          <select className="uf-input sm:col-span-1" value={form.team_a_id} onChange={(e) => setForm({ ...form, team_a_id: e.target.value })} required data-testid="match-team-a">
            <option value="">Équipe A</option>
            {filteredTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className="uf-input sm:col-span-1" value={form.team_b_id} onChange={(e) => setForm({ ...form, team_b_id: e.target.value })} required data-testid="match-team-b">
            <option value="">Équipe B</option>
            {filteredTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button type="submit" className="btn-brutal sm:col-span-1" data-testid="create-match-btn">Créer</button>
        </div>
        {filteredTeams.length < 2 && (
          <p className="text-xs text-[#FF9F0A] mt-3">Au moins 2 équipes validées pour cette discipline sont nécessaires.</p>
        )}
      </form>

      <div className="uf-card overflow-x-auto" data-testid="admin-matches-table">
        <table className="w-full text-sm">
          <thead className="bg-black border-b border-white/20 text-xs tracking-widest uppercase text-white/50">
            <tr>
              <th className="text-left p-3">Disc.</th>
              <th className="text-left p-3">Phase</th>
              <th className="text-left p-3">Équipe A</th>
              <th className="text-center p-3">Score</th>
              <th className="text-left p-3">Équipe B</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {matches.length === 0 ? (
              <tr><td colSpan={7} className="text-center p-10 text-white/40">Aucun match.</td></tr>
            ) : matches.map((m) => (
              <tr key={m.id} className="border-b border-white/5" data-testid={`admin-match-${m.id}`}>
                <td className="p-3 uppercase text-xs">{m.discipline}</td>
                <td className="p-3 uppercase text-xs">{m.phase}{m.pool ? ` · ${m.pool}` : ""}</td>
                <td className="p-3">{teamName(m.team_a_id)}</td>
                <td className="p-3">
                  <div className="flex gap-1 items-center justify-center">
                    <input type="number" min="0" className="uf-input !p-1 !w-14 text-center" defaultValue={m.score_a} onBlur={(e) => patch(m.id, { score_a: Number(e.target.value) })} data-testid={`score-a-${m.id}`} />
                    <span className="text-white/40">-</span>
                    <input type="number" min="0" className="uf-input !p-1 !w-14 text-center" defaultValue={m.score_b} onBlur={(e) => patch(m.id, { score_b: Number(e.target.value) })} data-testid={`score-b-${m.id}`} />
                  </div>
                </td>
                <td className="p-3">{teamName(m.team_b_id)}</td>
                <td className="p-3">
                  <select className="uf-input !py-1 !px-2 text-xs" value={m.status} onChange={(e) => patch(m.id, { status: e.target.value })} data-testid={`match-status-${m.id}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => remove(m.id)} className="text-[#FF3B30] text-xs uppercase tracking-widest hover:underline" data-testid={`delete-match-${m.id}`}>
                    Supprimer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function GalleryPanel() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/gallery");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image trop lourde (max 5 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFile(f);
      setPreview(reader.result);
    };
    reader.readAsDataURL(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!preview) {
      toast.error("Choisis une image");
      return;
    }
    setUploading(true);
    try {
      await api.post("/admin/gallery", { title: title || null, image_base64: preview });
      toast.success("Photo ajoutée");
      setTitle("");
      setFile(null);
      setPreview("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Photo supprimée");
    await load();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={upload} className="uf-card p-6" data-testid="gallery-upload-form">
        <h3 className="font-display text-2xl mb-4">Ajouter une photo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-start">
          <input
            className="uf-input"
            placeholder="Titre (optionnel)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="gallery-title"
          />
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="uf-input !p-2 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FF6B1A] file:text-black file:font-bold file:uppercase file:text-xs file:tracking-widest file:cursor-pointer"
            data-testid="gallery-file-input"
          />
          <button type="submit" disabled={uploading || !preview} className="btn-brutal" data-testid="gallery-upload-btn">
            {uploading ? "Envoi…" : "Uploader"}
          </button>
        </div>
        {preview && (
          <div className="mt-4">
            <img src={preview} alt="preview" className="max-h-48 border border-white/20" />
          </div>
        )}
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-gallery-grid">
        {items.length === 0 ? (
          <p className="text-white/40 col-span-full">Aucune photo pour l'instant.</p>
        ) : (
          items.map((it) => (
            <div key={it.id} className="uf-card overflow-hidden" data-testid={`admin-gallery-${it.id}`}>
              <div className="relative aspect-square">
                <img src={it.image_base64} alt={it.title || ""} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 flex items-center justify-between">
                <span className="text-xs truncate">{it.title || "—"}</span>
                <button
                  onClick={() => remove(it.id)}
                  className="text-[#FF3B30] text-xs uppercase tracking-widest hover:underline"
                  data-testid={`delete-gallery-${it.id}`}
                >
                  Suppr.
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ExportPanel() {
  const items = [
    { label: "Équipes", base: "/admin/export/teams", q: "", desc: "Toutes les équipes inscrites avec capitaine, téléphone et roster.", testid: "export-teams" },
    { label: "Inscrits Basket 3x3", base: "/admin/export/teams", q: "?discipline=basket", desc: "Équipes Basket uniquement, avec coordonnées des capitaines.", testid: "export-teams-basket" },
    { label: "Inscrits Football 5x5", base: "/admin/export/teams", q: "?discipline=foot", desc: "Équipes Football uniquement, avec coordonnées des capitaines.", testid: "export-teams-foot" },
    { label: "Classement global", base: "/admin/export/standings", q: "", desc: "Classement calculé toutes disciplines confondues.", testid: "export-standings-all" },
    { label: "Classement Basket 3x3", base: "/admin/export/standings", q: "?discipline=basket", desc: "Classement Basket uniquement.", testid: "export-standings-basket" },
    { label: "Classement Football 5x5", base: "/admin/export/standings", q: "?discipline=foot", desc: "Classement Football uniquement.", testid: "export-standings-foot" },
  ];

  const download = async (it, ext) => {
    try {
      const res = await api.get(`${it.base}.${ext}${it.q}`, { responseType: "blob" });
      const suffix = it.q ? "-" + it.q.split("=")[1] : "";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${it.base.split("/").pop()}${suffix}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export échoué");
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="export-panel">
      {items.map((it) => (
        <div key={it.base + it.q} className="uf-card p-6 flex flex-col">
          <h3 className="font-display text-2xl mb-2">{it.label}</h3>
          <p className="text-white/60 text-sm flex-1">{it.desc}</p>
          <div className="flex gap-3 mt-4">
            <button onClick={() => download(it, "pdf")} className="btn-brutal" data-testid={`${it.testid}-pdf`}>
              Télécharger PDF
            </button>
            <button
              onClick={() => download(it, "csv")}
              className="btn-brutal btn-brutal-ghost"
              data-testid={it.testid}
            >
              CSV
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function PartnersPanel() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/partners");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo trop lourd (max 2 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Nom du partenaire requis");
    if (!preview) return toast.error("Choisis un logo");
    setUploading(true);
    try {
      await api.post("/admin/partners", {
        name: name.trim(),
        website: website.trim() || null,
        logo_base64: preview,
      });
      toast.success("Partenaire ajouté");
      setName("");
      setWebsite("");
      setPreview("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce partenaire ?")) return;
    await api.delete(`/admin/partners/${id}`);
    toast.success("Partenaire supprimé");
    await load();
  };

  const [editing, setEditing] = useState(null); // partner being edited

  const saveEdit = async (patch) => {
    try {
      await api.patch(`/admin/partners/${editing.id}`, patch);
      toast.success("Partenaire mis à jour");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      {editing && (
        <PartnerEditModal partner={editing} onSave={saveEdit} onClose={() => setEditing(null)} />
      )}
      <form onSubmit={submit} className="uf-card p-6" data-testid="partner-upload-form">
        <h3 className="font-display text-2xl mb-4">Ajouter un partenaire</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="uf-input"
            placeholder="Nom du partenaire *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="partner-name"
          />
          <input
            className="uf-input"
            placeholder="Site web (optionnel) — https://…"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            data-testid="partner-website"
          />
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="uf-input !p-2 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FF6B1A] file:text-black file:font-bold file:uppercase file:text-xs file:tracking-widest file:cursor-pointer sm:col-span-2"
            data-testid="partner-file-input"
          />
        </div>
        {preview && (
          <div className="mt-4 bg-white/5 border border-white/10 p-4 inline-block">
            <img src={preview} alt="preview" className="max-h-24 object-contain" />
          </div>
        )}
        <div className="mt-4">
          <button type="submit" disabled={uploading || !preview} className="btn-brutal" data-testid="partner-upload-btn">
            {uploading ? "Envoi…" : "Ajouter le partenaire"}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-partners-grid">
        {items.length === 0 ? (
          <p className="text-white/40 col-span-full">Aucun partenaire ajouté. Les cartes par défaut s'affichent sur la home.</p>
        ) : (
          items.map((p) => (
            <div key={p.id} className="uf-card overflow-hidden" data-testid={`admin-partner-${p.id}`}>
              <div className="h-32 flex items-center justify-center bg-white/5 p-4">
                <img src={p.logo_base64} alt={p.name} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="p-3">
                <p className="font-bold text-sm truncate">{p.name}</p>
                {p.website && (
                  <p className="text-white/50 text-xs truncate">{p.website}</p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={() => setEditing(p)}
                    className="text-[#FF6B1A] text-xs uppercase tracking-widest hover:underline"
                    data-testid={`edit-partner-${p.id}`}
                  >
                    Modifier
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    className="text-[#FF3B30] text-xs uppercase tracking-widest hover:underline"
                    data-testid={`delete-partner-${p.id}`}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function HeadlinersPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", description: "", instagram: "", order: 0 });
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/headliners");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) {
      toast.error("Image trop lourde (max 3 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nom requis");
    if (!preview) return toast.error("Image requise");
    setUploading(true);
    try {
      await api.post("/admin/headliners", {
        ...form,
        order: Number(form.order) || 0,
        image_base64: preview,
      });
      toast.success("Tête d'affiche ajoutée");
      setForm({ name: "", role: "", description: "", instagram: "", order: 0 });
      setPreview("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette tête d'affiche ?")) return;
    await api.delete(`/admin/headliners/${id}`);
    toast.success("Supprimé");
    await load();
  };

  const updateOrder = async (id, order) => {
    await api.patch(`/admin/headliners/${id}`, { order: Number(order) || 0 });
    await load();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="uf-card p-6" data-testid="headliner-upload-form">
        <h3 className="font-display text-2xl mb-4">Ajouter une tête d'affiche</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="uf-input"
            placeholder="Nom de l'artiste *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="headliner-name"
          />
          <input
            className="uf-input"
            placeholder="Rôle (DJ, Artiste, Graffeur, Freestyler…)"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="headliner-role"
          />
          <textarea
            rows={2}
            className="uf-input sm:col-span-2"
            placeholder="Description (bio courte — optionnel)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="headliner-description"
          />
          <input
            className="uf-input"
            placeholder="Instagram (@pseudo ou URL, optionnel)"
            value={form.instagram}
            onChange={(e) => setForm({ ...form, instagram: e.target.value })}
            data-testid="headliner-instagram"
          />
          <input
            type="number"
            className="uf-input"
            placeholder="Ordre (0 = premier)"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: e.target.value })}
            data-testid="headliner-order"
          />
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="uf-input !p-2 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FF6B1A] file:text-black file:font-bold file:uppercase file:text-xs file:tracking-widest file:cursor-pointer sm:col-span-2"
            data-testid="headliner-file-input"
          />
        </div>
        {preview && (
          <div className="mt-4">
            <img src={preview} alt="preview" className="max-h-48 border border-white/20 rounded" />
          </div>
        )}
        <div className="mt-4">
          <button type="submit" disabled={uploading || !preview} className="btn-brutal" data-testid="headliner-upload-btn">
            {uploading ? "Envoi…" : "Ajouter"}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-headliners-grid">
        {items.length === 0 ? (
          <p className="text-white/40 col-span-full">Aucune tête d'affiche. La section n'apparaîtra pas sur la home tant qu'aucune n'est ajoutée.</p>
        ) : (
          items.map((h) => (
            <div key={h.id} className="uf-card overflow-hidden" data-testid={`admin-headliner-${h.id}`}>
              <div className="aspect-[4/5] bg-white/5">
                <img src={h.image_base64} alt={h.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-3 space-y-2">
                <p className="font-bold text-sm truncate">{h.name}</p>
                {h.role && <p className="text-xs text-[#FF6B1A] uppercase tracking-widest">{h.role}</p>}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] tracking-widest uppercase text-white/40">Ordre</label>
                  <input
                    type="number"
                    defaultValue={h.order}
                    onBlur={(e) => Number(e.target.value) !== h.order && updateOrder(h.id, e.target.value)}
                    className="uf-input !p-1 !w-14 text-xs"
                    data-testid={`headliner-order-${h.id}`}
                  />
                </div>
                <button
                  onClick={() => remove(h.id)}
                  className="text-[#FF3B30] text-xs uppercase tracking-widest hover:underline"
                  data-testid={`delete-headliner-${h.id}`}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


function PartnerEditModal({ partner, onSave, onClose }) {
  const [name, setName] = useState(partner.name || "");
  const [website, setWebsite] = useState(partner.website || "");
  const [logo, setLogo] = useState(""); // new logo if user picks one
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Logo trop lourd (max 2 Mo)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const patch = {};
    if (name.trim() !== partner.name) patch.name = name.trim();
    if ((website.trim() || null) !== partner.website) patch.website = website.trim() || null;
    if (logo) patch.logo_base64 = logo;
    if (Object.keys(patch).length === 0) {
      toast.message("Aucune modification");
      setSaving(false);
      onClose();
      return;
    }
    await onSave(patch);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="partner-edit-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="uf-card p-6 w-full max-w-lg space-y-4"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl">Modifier le partenaire</h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white text-xl" aria-label="Fermer">×</button>
        </div>

        <div className="flex items-center justify-center bg-white/5 border border-white/10 rounded p-4 min-h-[120px]">
          <img
            src={logo || partner.logo_base64}
            alt={partner.name}
            className="max-h-24 object-contain"
          />
        </div>

        <label className="block">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-2 block">Nom *</span>
          <input
            className="uf-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="edit-partner-name"
          />
        </label>
        <label className="block">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-2 block">Site web</span>
          <input
            className="uf-input"
            placeholder="https://…"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            data-testid="edit-partner-website"
          />
        </label>
        <label className="block">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-white/60 mb-2 block">Remplacer le logo (optionnel)</span>
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="uf-input !p-2 file:mr-3 file:py-1 file:px-3 file:border-0 file:bg-[#FF6B1A] file:text-black file:font-bold file:uppercase file:text-xs file:tracking-widest file:cursor-pointer"
            data-testid="edit-partner-file"
          />
        </label>

        <div className="flex gap-3 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs tracking-widest uppercase text-white/60 hover:text-white px-4 py-2"
          >
            Annuler
          </button>
          <button type="submit" disabled={saving} className="btn-brutal" data-testid="edit-partner-save">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}


function BracketPanel({ onChange }) {
  const [discipline, setDiscipline] = useState("basket");
  const [topN, setTopN] = useState(8);
  const [brackets, setBrackets] = useState({ basket: {}, foot: {} });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    const [b, f] = await Promise.all([
      api.get("/bracket/basket"),
      api.get("/bracket/foot"),
    ]);
    setBrackets({ basket: b.data, foot: f.data });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    if (!window.confirm(`Générer un bracket ${topN} équipes pour ${discipline} ? Cela supprimera tout bracket existant pour cette discipline.`)) return;
    setLoading(true);
    try {
      await api.post("/admin/bracket/generate", { discipline, top_n: Number(topN) });
      toast.success("Bracket généré depuis le classement");
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const nextRound = async (d) => {
    setLoading(true);
    try {
      const res = await api.post(`/admin/bracket/next-round?discipline=${d}`);
      toast.success(`Phase ${res.data.phase} créée avec ${res.data.matches_created} matchs`);
      await load();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const PHASE_ORDER = ["8e", "quart", "demi", "finale"];
  const PHASE_LABELS = { "8e": "8èmes", quart: "Quarts", demi: "Demi-finales", finale: "Finale" };

  return (
    <div className="space-y-6" data-testid="bracket-panel">
      <div className="uf-card p-6">
        <h3 className="font-display text-2xl mb-4">Générer le bracket éliminatoire</h3>
        <p className="text-white/60 text-sm mb-4">
          À partir du classement calculé sur les matchs de poule (terminés). Prend les N meilleures équipes et crée les matchs de la première phase.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select className="uf-input" value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="bracket-discipline">
            <option value="basket">Basket 3v3</option>
            <option value="foot">Football 5v5</option>
          </select>
          <select className="uf-input" value={topN} onChange={(e) => setTopN(Number(e.target.value))} data-testid="bracket-topn">
            <option value={4}>Top 4 → demi-finales</option>
            <option value={8}>Top 8 → quarts de finale</option>
            <option value={16}>Top 16 → 8èmes de finale</option>
          </select>
          <button onClick={generate} disabled={loading} className="btn-brutal" data-testid="bracket-generate-btn">
            {loading ? "…" : "Générer"}
          </button>
        </div>
      </div>

      {["basket", "foot"].map((d) => {
        const phases = brackets[d] || {};
        const hasBracket = Object.keys(phases).length > 0;
        // Find latest phase to check if next round is possible
        const activePhases = PHASE_ORDER.filter((p) => phases[p]?.length);
        const lastPhase = activePhases[activePhases.length - 1];
        const lastMatches = lastPhase ? phases[lastPhase] : [];
        const allFinished = lastMatches.length > 0 && lastMatches.every((m) => m.status === "finished" && m.score_a !== m.score_b);
        const canAdvance = allFinished && lastPhase !== "finale";

        return (
          <div key={d} className="uf-card p-6" data-testid={`bracket-view-${d}`}>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-display text-2xl">
                {d === "basket" ? "Basket 3v3" : "Football 5v5"}
              </h3>
              {hasBracket && canAdvance && (
                <button
                  onClick={() => nextRound(d)}
                  disabled={loading}
                  className="text-xs tracking-widest uppercase border border-[#FF6B1A] text-[#FF6B1A] px-4 py-2 rounded-full hover:bg-[#FF6B1A] hover:text-black transition-colors"
                  data-testid={`next-round-${d}`}
                >
                  → Générer la phase suivante
                </button>
              )}
            </div>
            {!hasBracket ? (
              <p className="text-white/40 text-sm">Aucun bracket généré pour cette discipline.</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex gap-4 min-w-max">
                  {PHASE_ORDER.map((phase) => {
                    const matches = phases[phase] || [];
                    if (matches.length === 0) return null;
                    return (
                      <div key={phase} className="flex-1 min-w-[220px]">
                        <p className="text-xs tracking-[0.2em] uppercase text-[#FF6B1A] mb-3 text-center">{PHASE_LABELS[phase]}</p>
                        <div className="space-y-3 flex flex-col justify-around h-full">
                          {matches.map((m) => (
                            <BracketMatchCard key={m.id} match={m} />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function BracketMatchCard({ match }) {
  const winnerA = match.status === "finished" && match.score_a > match.score_b;
  const winnerB = match.status === "finished" && match.score_b > match.score_a;
  return (
    <div className="uf-card p-3 !bg-black border border-white/10" data-testid={`bracket-match-${match.id}`}>
      <div className={`flex items-center justify-between py-1 ${winnerA ? "text-[#FF6B1A] font-bold" : ""}`}>
        <span className="text-sm truncate">{match.team_a_name || "TBD"}</span>
        <span className="font-display text-lg">{match.score_a}</span>
      </div>
      <div className="border-t border-white/10 my-1" />
      <div className={`flex items-center justify-between py-1 ${winnerB ? "text-[#FF6B1A] font-bold" : ""}`}>
        <span className="text-sm truncate">{match.team_b_name || "TBD"}</span>
        <span className="font-display text-lg">{match.score_b}</span>
      </div>
      <p className="text-[10px] tracking-widest uppercase text-white/40 mt-1 text-center">
        {match.status === "finished" ? "Terminé" : match.status === "live" ? "● LIVE" : "À venir"}
      </p>
    </div>
  );
}


function StandingsPanel() {
  const [discipline, setDiscipline] = useState("basket");
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const generate = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/standings", { params: { discipline: d } });
      setStandings(data);
      setGeneratedAt(new Date());
      if (data.length === 0) {
        toast.info("Aucune équipe validée pour cette discipline.");
      } else {
        toast.success(`Classement ${d} mis à jour (${data.length} équipes)`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    generate(discipline);
  }, [discipline, generate]);

  const label = discipline === "basket" ? "Basket 3v3" : "Football 5v5";

  return (
    <div className="space-y-6" data-testid="standings-panel">
      <div className="uf-card p-6">
        <h3 className="font-display text-2xl mb-4">Classement automatique</h3>
        <p className="text-white/60 text-sm mb-4">
          Calculé automatiquement à partir des scores des matchs terminés (3 pts victoire, 1 pt nul).
          Départage : points, puis différence de points, puis points marqués.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <select
            className="uf-input"
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            data-testid="standings-discipline"
          >
            <option value="basket">Basket 3v3</option>
            <option value="foot">Football 5v5</option>
          </select>
          <button
            onClick={() => generate(discipline)}
            disabled={loading}
            className="btn-brutal"
            data-testid="standings-generate-btn"
          >
            {loading ? "…" : "Générer / actualiser le classement"}
          </button>
        </div>
        {generatedAt && (
          <p className="text-xs text-white/40 mt-3" data-testid="standings-updated-at">
            Dernière mise à jour : {generatedAt.toLocaleTimeString("fr-FR")}
          </p>
        )}
      </div>

      <div className="uf-card overflow-x-auto" data-testid="standings-admin-table">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h4 className="font-display text-xl">{label}</h4>
          <span className="text-xs tracking-widest uppercase text-white/40">{standings.length} équipes</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-black border-b border-white/20">
            <tr className="text-xs tracking-widest uppercase text-white/50">
              <th className="text-left p-3">#</th>
              <th className="text-left p-3">Équipe</th>
              <th className="text-left p-3 hidden sm:table-cell">Quartier</th>
              <th className="p-3">J</th>
              <th className="p-3">V</th>
              <th className="p-3">N</th>
              <th className="p-3">D</th>
              <th className="p-3 hidden sm:table-cell">Pour</th>
              <th className="p-3 hidden sm:table-cell">Contre</th>
              <th className="p-3">+/-</th>
              <th className="p-3 text-[#FF6B1A]">Pts</th>
            </tr>
          </thead>
          <tbody>
            {standings.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center p-10 text-white/40">
                  Aucune équipe classée. Validez des équipes puis saisissez les scores des matchs.
                </td>
              </tr>
            ) : (
              standings.map((s, i) => (
                <tr
                  key={s.team_id}
                  className="border-b border-white/5 hover:bg-white/5"
                  data-testid={`admin-standing-row-${i}`}
                >
                  <td className="p-3 font-display text-xl">{i + 1}</td>
                  <td className="p-3 font-bold">{s.team_name}</td>
                  <td className="p-3 text-white/60 hidden sm:table-cell">{s.quartier}</td>
                  <td className="p-3 text-center">{s.played}</td>
                  <td className="p-3 text-center text-[#FF6B1A]">{s.wins}</td>
                  <td className="p-3 text-center">{s.draws}</td>
                  <td className="p-3 text-center text-[#FF3B30]">{s.losses}</td>
                  <td className="p-3 text-center hidden sm:table-cell">{s.points_for}</td>
                  <td className="p-3 text-center hidden sm:table-cell">{s.points_against}</td>
                  <td className="p-3 text-center">
                    {s.points_for - s.points_against > 0 ? "+" : ""}
                    {s.points_for - s.points_against}
                  </td>
                  <td className="p-3 text-center font-display text-2xl text-[#FF6B1A]">{s.points}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
